import React, { useState, useMemo } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { ChevronLeft, ChevronRight, Clock, MapPin, Calendar } from 'lucide-react';

// ── Mock combined squad sessions (in production: fetched from all squad providers) ──
const SQUAD_COLORS = {
  'Squad Alpha': { dot: 'bg-blue-500',  badge: 'bg-blue-100 text-blue-700',  border: 'border-l-blue-500' },
  'Squad Beta':  { dot: 'bg-green-500', badge: 'bg-green-100 text-green-700', border: 'border-l-green-500' },
};

const MOCK_COMBINED_SESSIONS = [
  { id: 1,  squad: 'Squad Alpha', title: 'Morning Practice',        date: '2026-05-22', time: '08:00', duration: 90,  location: 'Court A',       type: 'practice' },
  { id: 2,  squad: 'Squad Beta',  title: 'Fundamentals Drill',      date: '2026-05-22', time: '10:00', duration: 90,  location: 'Court B',       type: 'practice' },
  { id: 3,  squad: 'Squad Alpha', title: 'Match Play',              date: '2026-05-24', time: '09:00', duration: 120, location: 'Courts A & B',  type: 'match'    },
  { id: 4,  squad: 'Squad Beta',  title: 'Fitness & Footwork',      date: '2026-05-25', time: '09:00', duration: 60,  location: 'Training Room', type: 'practice' },
  { id: 5,  squad: 'Squad Alpha', title: 'Video Analysis',          date: '2026-05-26', time: '17:00', duration: 60,  location: 'Meeting Room',  type: 'meeting'  },
  { id: 6,  squad: 'Squad Alpha', title: 'Weekend Tournament Prep', date: '2026-05-28', time: '08:00', duration: 180, location: 'All Courts',    type: 'practice' },
  { id: 7,  squad: 'Squad Alpha', title: 'Serve & Return Drills',   date: '2026-06-02', time: '08:00', duration: 90,  location: 'Court A',       type: 'practice' },
  { id: 8,  squad: 'Squad Beta',  title: 'Footwork Patterns',       date: '2026-06-02', time: '10:30', duration: 60,  location: 'Court B',       type: 'practice' },
  { id: 9,  squad: 'Squad Alpha', title: 'Tactics Session',         date: '2026-06-05', time: '16:00', duration: 90,  location: 'Courts A & B',  type: 'match'    },
  { id: 10, squad: 'Squad Beta',  title: 'Conditioning',            date: '2026-06-07', time: '09:00', duration: 60,  location: 'Training Room', type: 'practice' },
];

const SESSION_TYPE_COLORS = {
  practice: 'bg-blue-100 text-blue-700',
  match:    'bg-red-100 text-red-700',
  meeting:  'bg-purple-100 text-purple-700',
  event:    'bg-green-100 text-green-700',
};

function fmt12(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return m === 0 ? `${h12} ${suffix}` : `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
}

// Returns 'YYYY-MM-DD' for a Date object in local time
function toDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function ProgramSchedulePanel({ program, userRole }) {
  const { theme } = useTheme();
  const today = new Date();
  const todayKey = toDateKey(today);

  const [curYear, setCurYear]   = useState(today.getFullYear());
  const [curMonth, setCurMonth] = useState(today.getMonth()); // 0-indexed
  const [selectedDay, setSelectedDay] = useState(todayKey);

  const sessions = MOCK_COMBINED_SESSIONS;

  // Index sessions by date key
  const byDate = useMemo(() => {
    const map = {};
    sessions.forEach(s => {
      if (!map[s.date]) map[s.date] = [];
      map[s.date].push(s);
    });
    return map;
  }, [sessions]);

  // Calendar grid for current month
  const calendarDays = useMemo(() => {
    const firstDay = new Date(curYear, curMonth, 1).getDay(); // 0=Sun
    const daysInMonth = new Date(curYear, curMonth + 1, 0).getDate();
    const cells = [];
    // Leading blanks
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const key = `${curYear}-${String(curMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      cells.push({ day: d, key });
    }
    return cells;
  }, [curYear, curMonth]);

  const prevMonth = () => {
    if (curMonth === 0) { setCurMonth(11); setCurYear(y => y - 1); }
    else setCurMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (curMonth === 11) { setCurMonth(0); setCurYear(y => y + 1); }
    else setCurMonth(m => m + 1);
  };

  const selectedSessions = byDate[selectedDay] || [];

  // Upcoming sessions (from today, sorted)
  const upcoming = useMemo(() =>
    sessions
      .filter(s => s.date >= todayKey)
      .sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time))
      .slice(0, 10),
    [sessions, todayKey]
  );

  return (
    <div className="space-y-6">
      {/* Squad legend */}
      <div className="flex flex-wrap items-center gap-3">
        <span className={`text-xs font-medium ${theme.textMuted} uppercase tracking-wide`}>Squads:</span>
        {Object.entries(SQUAD_COLORS).map(([name, cfg]) => (
          <span key={name} className="flex items-center gap-1.5 text-xs">
            <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} />
            <span className={theme.textSecondary}>{name}</span>
          </span>
        ))}
      </div>

      {/* Calendar */}
      <div className={`${theme.card} border ${theme.border} rounded-xl overflow-hidden`}>
        {/* Month nav */}
        <div className={`flex items-center justify-between px-5 py-3 border-b ${theme.border}`}>
          <button onClick={prevMonth} className={`p-1.5 rounded-lg ${theme.cardHover} transition`}>
            <ChevronLeft size={16} className={theme.textSecondary} />
          </button>
          <h4 className={`font-semibold text-sm ${theme.text}`}>
            {MONTHS[curMonth]} {curYear}
          </h4>
          <button onClick={nextMonth} className={`p-1.5 rounded-lg ${theme.cardHover} transition`}>
            <ChevronRight size={16} className={theme.textSecondary} />
          </button>
        </div>

        {/* Weekday headers */}
        <div className="grid grid-cols-7 border-b border-gray-100 dark:border-gray-800">
          {WEEKDAYS.map(d => (
            <div key={d} className={`py-2 text-center text-xs font-medium ${theme.textMuted}`}>{d}</div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7">
          {calendarDays.map((cell, i) => {
            if (!cell) return <div key={`blank-${i}`} className={`min-h-[60px] border-b border-r border-gray-100 dark:border-gray-800`} />;

            const isToday = cell.key === todayKey;
            const isSelected = cell.key === selectedDay;
            const daySessions = byDate[cell.key] || [];
            // Unique squads that have sessions on this day
            const squadsOnDay = [...new Set(daySessions.map(s => s.squad))];

            return (
              <button
                key={cell.key}
                onClick={() => setSelectedDay(cell.key)}
                className={`min-h-[60px] p-1.5 flex flex-col items-center border-b border-r border-gray-100 dark:border-gray-800 transition
                  ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : theme.cardHover}`}
              >
                <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-medium mb-1
                  ${isToday ? 'bg-blue-500 text-white' : isSelected ? 'text-blue-600 font-bold' : theme.text}`}>
                  {cell.day}
                </span>
                {/* Squad dots */}
                <div className="flex gap-0.5 flex-wrap justify-center">
                  {squadsOnDay.map(squad => (
                    <span key={squad} className={`w-1.5 h-1.5 rounded-full ${SQUAD_COLORS[squad]?.dot || 'bg-gray-400'}`} />
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected day sessions */}
      <div>
        <h4 className={`text-sm font-semibold ${theme.text} mb-3`}>
          {selectedDay === todayKey ? 'Today' : new Date(selectedDay + 'T00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          {selectedSessions.length > 0 && (
            <span className={`ml-2 text-xs font-normal ${theme.textMuted}`}>
              {selectedSessions.length} session{selectedSessions.length !== 1 ? 's' : ''}
            </span>
          )}
        </h4>

        {selectedSessions.length === 0 ? (
          <div className={`text-center py-8 ${theme.card} border ${theme.border} rounded-xl`}>
            <Calendar size={28} className={`mx-auto mb-2 ${theme.textMuted} opacity-30`} />
            <p className={`text-sm ${theme.textMuted}`}>No sessions on this day</p>
          </div>
        ) : (
          <div className="space-y-2">
            {selectedSessions
              .sort((a, b) => a.time.localeCompare(b.time))
              .map(s => {
                const colors = SQUAD_COLORS[s.squad] || { border: 'border-l-gray-400', badge: 'bg-gray-100 text-gray-600' };
                return (
                  <div key={s.id} className={`${theme.card} border ${theme.border} border-l-4 ${colors.border} rounded-xl p-4`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className={`font-semibold text-sm ${theme.text}`}>{s.title}</span>
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${colors.badge}`}>{s.squad}</span>
                        </div>
                        <div className={`flex flex-wrap items-center gap-3 text-xs ${theme.textSecondary}`}>
                          <span className="flex items-center gap-1"><Clock size={11} />{fmt12(s.time)} · {s.duration} min</span>
                          <span className="flex items-center gap-1"><MapPin size={11} />{s.location}</span>
                        </div>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${SESSION_TYPE_COLORS[s.type] || 'bg-gray-100 text-gray-600'}`}>
                        {s.type.charAt(0).toUpperCase() + s.type.slice(1)}
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Upcoming sessions list */}
      <div>
        <h4 className={`text-sm font-semibold ${theme.text} mb-3`}>Upcoming Sessions</h4>
        {upcoming.length === 0 ? (
          <p className={`text-sm ${theme.textMuted}`}>No upcoming sessions.</p>
        ) : (
          <div className="space-y-2">
            {upcoming.map(s => {
              const d = new Date(s.date + 'T00:00');
              const colors = SQUAD_COLORS[s.squad] || { border: 'border-l-gray-400', badge: 'bg-gray-100 text-gray-600' };
              return (
                <button
                  key={s.id}
                  onClick={() => setSelectedDay(s.date)}
                  className={`w-full flex items-center gap-4 p-3 ${theme.card} border ${theme.border} border-l-4 ${colors.border} rounded-xl ${theme.cardHover} transition text-left`}
                >
                  <div className="text-center w-10 flex-shrink-0">
                    <div className={`text-xs font-medium ${theme.textMuted} uppercase`}>
                      {d.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div className={`text-base font-bold ${theme.text}`}>{d.getDate()}</div>
                    <div className={`text-xs ${theme.textMuted}`}>
                      {d.toLocaleDateString('en-US', { month: 'short' })}
                    </div>
                  </div>
                  <div className={`w-px self-stretch bg-gray-200 dark:bg-gray-700`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-medium text-sm ${theme.text} truncate`}>{s.title}</span>
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${colors.badge}`}>{s.squad}</span>
                    </div>
                    <div className={`flex flex-wrap items-center gap-3 text-xs ${theme.textSecondary} mt-0.5`}>
                      <span className="flex items-center gap-1"><Clock size={10} />{fmt12(s.time)} · {s.duration} min</span>
                      <span className="flex items-center gap-1"><MapPin size={10} />{s.location}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
